import {usePlugins} from '@github-ui/copilot-chat/plugin/registry'
import {isFileReference as isFileRef, referencesAreEqual} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import type {CopilotChatReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {useChatStateLens} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useTrackingRef} from '@github-ui/use-tracking-ref'
import {useCallback} from 'react'

import {
  makeItemForPluginSupportedReference,
  makeItemFromFileDiffReference,
  makeItemFromFileReference,
  makeItemFromIssueReference,
  makeItemFromJobReference,
  makeVersionedItemFromReference,
  type PreviewableContent,
} from '../components/ContentPreview/content-preview-types'
import {useContentPreview} from '../components/ContentPreview/ContentPreviewContext'

function isRepositoryReference(
  ref: CopilotChatReference,
): ref is CopilotChatReference & {type: 'repository'; id: number} {
  return ref.type === 'repository' && typeof (ref as Record<string, unknown>).id === 'number'
}

function isFileReference(ref: CopilotChatReference): boolean {
  return ref.type === 'file' || ref.type === 'thread-scoped-file' || ref.type === 'file-diff'
}

export interface UseOnReferenceSelectProps {
  messageId?: string
  messageIndex: number
  messageTimestamp?: string
}

export function useOnReferenceSelect({messageId, messageIndex, messageTimestamp}: UseOnReferenceSelectProps) {
  const {openPreviewPane, openItem, updateItem, openResourcePanel, setResourcePanelRepoId, items} = useContentPreview()
  const itemsRef = useTrackingRef(items)
  const plugins = usePlugins()
  const isEmbedded = useChatStateLens(s => s.mode === 'assistive')
  const optedInToPreviewFeatures = useChatStateLens(s => s.optedInToPreviewFeatures)

  return useCallback(
    (ref: CopilotChatReference, event?: React.MouseEvent<HTMLAnchorElement>) => {
      // In embedded (assistive) mode, let repo and regular file references navigate
      // via the default <a href> link behavior instead of opening previews.
      // Thread-scoped files (pasted attachments) are an exception when `prettifyPastedCode` is enabled, so as not to route to /copilot
      const isThreadScopedFileException = copilotFeatureFlags.prettifyPastedCode && ref.type === 'thread-scoped-file'
      if (isEmbedded && !isThreadScopedFileException && (isRepositoryReference(ref) || isFileReference(ref))) {
        return
      }

      // When the resource panel flag is enabled, clicking a repo reference should
      // open the resource panel with that repo selected instead of navigating.
      if (copilotFeatureFlags.copilotResourcePanel && optedInToPreviewFeatures && isRepositoryReference(ref)) {
        event?.preventDefault()
        setResourcePanelRepoId({repoId: ref.id})
        openPreviewPane()
        openResourcePanel()
        sendEvent('dotcom_chat.activate', {target: 'RESOURCE_PANEL_OPEN', mode: 'immersive', source: 'reference_pill'})
        return
      }

      // if the reference is one we can show in the browser, do
      // If a matching item already exists in the preview (e.g. opened from ResourcePanel),
      // just select its tab instead of creating a new item that gets stuck loading.
      if (isFileRef(ref)) {
        for (const [id, item] of itemsRef.current) {
          if (item.type === 'file' && isFileRef(item.reference) && referencesAreEqual(item.reference, ref)) {
            event?.preventDefault()
            openItem(id)
            openPreviewPane()
            return
          }
        }
      }

      let item: PreviewableContent | null = null
      switch (ref.type) {
        case 'file':
          item = makeItemFromFileReference(ref, messageId, messageTimestamp)
          break
        case 'file-diff':
          item = makeItemFromFileDiffReference(ref, messageId, messageTimestamp)
          break
        case 'thread-scoped-file':
          item = makeVersionedItemFromReference({ref, messageIndex, messageId, timestamp: messageTimestamp})
          break
        case 'draft-issue':
        case 'draft-update-issue':
          item = makeVersionedItemFromReference({ref, messageIndex, messageId})
          break
        case 'issue':
          item = makeItemFromIssueReference(ref, messageId)
          break
        case 'job':
          if (copilotFeatureFlags.immersiveJobResultPreview) {
            item = makeItemFromJobReference(ref, messageId)
          }
          break
        default:
          for (const plugin of plugins) {
            if (plugin.previewComponents?.[ref.type]) {
              item = makeItemForPluginSupportedReference(ref, messageId)
              break
            }
          }
      }

      if (!item) return

      event?.preventDefault()
      updateItem(item)
      openItem(item.id)
      openPreviewPane()
    },
    [
      isEmbedded,
      itemsRef,
      optedInToPreviewFeatures,
      messageId,
      messageIndex,
      messageTimestamp,
      plugins,
      openPreviewPane,
      openItem,
      updateItem,
      openResourcePanel,
      setResourcePanelRepoId,
    ],
  )
}
